import './StandistForm.css';

import type { FormInstance }         from 'antd';
import React                         from 'react';
import { Button }                    from 'antd';
import { Form }                      from 'antd';
import { Col }                       from 'antd';
import { observer }                  from 'mobx-react';
import GridInfo                      from 'components/App/GridInfo';
import Row                           from 'components/App/Row';
import type { StandistRole }         from 'constants/StandistRoleReference';
import StandistByStandistModel       from 'models/StandistByStandistModel';
import UserStandRoleModel            from 'models/UserStandRoleModel';
import { appStore }                  from 'stores/AppStore';
import handleApiError                from 'utils/handleApiError';
import FormattedMessageWrapper       from 'components/App/FormattedMessageWrapper';
import Input                         from 'components/App/IntlInput';
import InputPassword                 from 'components/App/IntlInputPassword';
import Select                        from 'components/App/IntlSelect';
import { EN, FR }                    from 'constants/Languages';
import YesOrNo                       from 'components/App/YesOrNo';
import UserModel                     from 'models/UserModel';
import { messageErrorIntl }          from 'utils/messageIntl';
import StandistSelect                from './StandistSelect';
import handleFormError               from 'utils/handleFormError';
import { ROLE_STANDIST }             from 'constants/RoleReferences';
import { Collection }                from '@mathquis/modelx';
import StandModel                    from 'models/StandModel';
import RoleModel                     from 'models/RoleModel';
import { AppFormComponent }          from 'components/App/AppForm';
import type { AppFormInjectedProps } from 'components/App/AppForm';

const { Option } = Select;

export const MODE_ADD = 'add';
export const MODE_ADD_FOR_EXHIBITOR = 'add_for_exhibitor';
export const MODE_EDIT = 'edit';
export const MODE_VIEW = 'view';

export type STANDIST_FORM_MODES = 'add' | 'add_for_exhibitor' | 'edit' | 'view';

interface StandistFormProps {
  mode: STANDIST_FORM_MODES;
  stand: StandModel;
  standistCollection: Collection<UserModel>;
  standistRole: StandistRole;
  user: UserModel;
  userStandRole: UserStandRoleModel;
}

interface StandistFormState {
  loading: boolean;
  selectedUserIri?: string;
}

@observer
class StandistForm extends AppFormComponent<StandistFormProps, StandistFormState> {
  public currentUser = new UserModel();

  public refForm: React.RefObject<FormInstance> = React.createRef<FormInstance>();

  public state: StandistFormState = {
    loading: false,
    selectedUserIri: undefined,
  };

  public componentDidMount() {
    const { user } = this.props;

    this.currentUser.set(user.attributes);

    const initialValues = this.getInitialValues();
    this.refForm.current?.setFieldsValue(initialValues);

    this.setState({ selectedUserIri: user.iri });

    this.updateFooter();
  }

  public componentDidUpdate(
    _prevProps: StandistFormProps & AppFormInjectedProps,
    prevState: StandistFormState,
  ) {
    if (prevState.loading !== this.state.loading) {
      this.updateFooter();
    }
  }

  private updateFooter = () => {
    const { closeModal, mode, user } = this.props;
    const { loading } = this.state;

    if (mode === MODE_VIEW) {
      this.props.setModalFooter([
        <Button key="ok" onClick={closeModal} type="primary">
          <FormattedMessageWrapper id="OK" />
        </Button>,
      ]);
      return;
    }

    this.props.setModalFooter(
      [
        <Button
          className="modal-btn-back"
          htmlType="button"
          key="back"
          loading={loading}
          onClick={closeModal}
        >
          <FormattedMessageWrapper id="Annuler" />
        </Button>,

        user.id ? (
          <Button
            danger
            htmlType="button"
            key="delete"
            loading={loading}
            onClick={this.removeStandist}
          >
            <FormattedMessageWrapper id="Retirer l'utilisateur" />
          </Button>
        ) : null,

        <Button
          htmlType="button"
          key="ok"
          loading={loading}
          onClick={this.submit}
          style={{ float: 'right' }}
          type="primary"
        >
          <FormattedMessageWrapper id="Enregistrer" />
        </Button>,
      ].filter(Boolean) as React.ReactNode[],
    );
  };

  public render() {
    const { mode, user, userStandRole } = this.props;

    if (mode === MODE_VIEW) {
      return (
        <GridInfo
          data={[
            [
              {
                label: <FormattedMessageWrapper id="Société" />,
                value: userStandRole.company || user.company,
              },
              {
                label: <FormattedMessageWrapper id="Nom" />,
                value: userStandRole.lastName || user.lastName,
              },
              {
                label: <FormattedMessageWrapper id="Prénom" />,
                value: userStandRole.firstName || user.firstName,
              },
              {
                label: <FormattedMessageWrapper id="Email" />,
                value: user.email,
              },
              {
                label: <FormattedMessageWrapper id="Téléphone" />,
                value: userStandRole.phone || user.phone,
              },
            ],
          ]}
        />
      );
    }

    return this.renderStandistForm();
  }

  public renderStandistForm = () => {
    const { mode } = this.props;

    return (
      <div className="antd-form-custom-style modal-form">
        {mode === MODE_ADD && this.renderStandistSelector()}

        <Form
          ref={this.refForm}
        >
          <fieldset>
            <legend>
              {this.currentUser.id ? (
                <span style={{ color: '#e74c3c' }}>
                  <FormattedMessageWrapper
                    id="Modification coordonnées"
                    values={{
                      firstName: this.currentUser.firstName,
                      lastName: this.currentUser.lastName,
                    }}
                  />
                </span>
              ) : (
                <span style={{ color: '#27ae60' }}>
                  <FormattedMessageWrapper id="Création d'un nouvel utilisateur" />
                </span>
              )}
            </legend>

            <Row>
              <Col lg={12} md={24} span={12}>
                <Form.Item
                  label={<FormattedMessageWrapper id="Société" />}
                  labelCol={{ span: 24 }}
                  name="company"
                  rules={[
                    {
                      message: <FormattedMessageWrapper id="Société manquante" />,
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col lg={12} md={24} span={12}>
                <Form.Item
                  label={<FormattedMessageWrapper id="Nom" />}
                  labelCol={{ span: 24 }}
                  name="lastName"
                  rules={[
                    {
                      message: <FormattedMessageWrapper id="Nom manquant" />,
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={12} md={24} span={12}>
                <Form.Item
                  label={<FormattedMessageWrapper id="Prénom" />}
                  labelCol={{ span: 24 }}
                  name="firstName"
                  rules={[
                    {
                      message: <FormattedMessageWrapper id="Prénom manquant" />,
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={12} md={24} span={12}>
                <Form.Item
                  label={<FormattedMessageWrapper id="Email" />}
                  labelCol={{ span: 24 }}
                  name="email"
                  rules={[
                    {
                      message: <FormattedMessageWrapper id="Email manquant" />,
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={12} md={24} span={12}>
                <Form.Item
                  label={<FormattedMessageWrapper id="Téléphone" />}
                  labelCol={{ span: 24 }}
                  name="phone"
                  rules={[
                    {
                      message: <FormattedMessageWrapper id="Téléphone manquant" />,
                      required: false,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            {(this.currentUser.id && mode !== MODE_ADD_FOR_EXHIBITOR) && (
              <Row>
                <Col lg={12} md={24} span={12}>
                  <Form.Item
                    label={<FormattedMessageWrapper id="Mot de passe" />}
                    labelCol={{ span: 24 }}
                    name="plainPassword"
                    rules={[
                      {
                        message: <FormattedMessageWrapper id="Mot de passe manquant" />,
                        required: false,
                      },
                    ]}
                  >
                    <InputPassword autoComplete="new-password" />
                  </Form.Item>
                </Col>
                <Col lg={12} md={24} span={12}>
                  <Form.Item
                    label={<FormattedMessageWrapper id="Confirmation mot de passe" />}
                    labelCol={{ span: 24 }}
                    name="plainPasswordConfirmation"
                    rules={[
                      {
                        message: (
                          <FormattedMessageWrapper id="Confirmation mot de passe manquante" />
                        ),
                        required: false,
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('plainPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Le mot de passe ne correspond pas.'));
                        },
                      }),
                    ]}
                  >
                    <InputPassword autoComplete="new-password" />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row>
              <Col lg={12} md={24}>
                <Form.Item
                  label={<FormattedMessageWrapper id="Préférence linguistique" />}
                  labelCol={{ span: 24 }}
                  name="languagePreference"
                >
                  <Select placeholder="Préférence linguistique" style={{ width: '100%' }}>
                    <Option value={null}>
                      <FormattedMessageWrapper id="Aucune" />
                    </Option>
                    <Option value={FR.value}>{FR.label}</Option>
                    <Option value={EN.value}>{EN.label}</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {!this.currentUser.id && (
              <>
                <br />
                <Row>
                  <Col span={24}>
                    <i>
                      <FormattedMessageWrapper
                        id="Un mot de passe aléatoire sera automatiquement généré pour cet utilisateur." />
                    </i>
                  </Col>
                </Row>
                <br />
              </>
            )}

            <Row
              style={{
                display: mode !== MODE_ADD_FOR_EXHIBITOR ? 'block' : 'none',
              }}
            >
              <Col span={24}>
                <Form.Item
                  label={<FormattedMessageWrapper id="Notifier cet utilisateur" />}
                  labelCol={{ span: 24 }}
                  name="notify"
                  rules={[
                    {
                      message: <FormattedMessageWrapper id="Information manquante" />,
                      required: true,
                    },
                  ]}
                >
                  <YesOrNo />
                </Form.Item>
              </Col>
            </Row>
          </fieldset>
        </Form>
      </div>
    );
  };

  public renderStandistSelector = () => {
    const { standistCollection } = this.props;

    return (
      <Row>
        <Col className="no-margin" span={24}>
          <div className="input-label">
            <FormattedMessageWrapper id="Rechercher un utilisateur existant :" />
          </div>
        </Col>
        <Col className="reverse-margin" lg={14} md={24} span={14}>
          <StandistSelect
            onStandistSelect={(selectedUser: UserModel) => {
              this.currentUser.set(selectedUser.attributes);

              this.refForm.current?.setFieldsValue({
                company: selectedUser.company,
                email: selectedUser.email,
                firstName: selectedUser.firstName,
                lastName: selectedUser.lastName,
                phone: selectedUser.phone,
              });

              this.setState({
                selectedUserIri: selectedUser.iri,
              });
            }}
            selectedStandistIri={this.state.selectedUserIri}
            standistCollection={standistCollection}
          />
        </Col>
        <Col className="reverse-margin" lg={4} md={24} span={4}>
          <div style={{ lineHeight: 2, textAlign: 'center' }}>
            <FormattedMessageWrapper id="ou" />
          </div>
        </Col>
        <Col className="reverse-margin" lg={6} md={24} span={6} style={{ textAlign: 'center' }}>
          <Button
            className="btn-clear-data"
            htmlType="button"
            key="clear"
            onClick={() => {
              this.refForm.current?.resetFields();
            }}
          >
            <FormattedMessageWrapper id="Nouvel utilisateur" />
          </Button>
        </Col>
      </Row>
    );
  };

  public submit = async () => {
    const { mode } = this.props;

    this.setState({ loading: true });

    try {
      if (mode === MODE_ADD_FOR_EXHIBITOR) {
        await this.handleSubmitSpecial();
      } else {
        await this.handleSubmit();
      }
    } catch (error) {
      handleFormError(error, this.refForm.current);
    } finally {
      this.setState({ loading: false });
    }
  };

  private getInitialValues = () => {
    const { user, userStandRole } = this.props;

    return {
      company: userStandRole.company || user.company,
      email: user.email,
      firstName: userStandRole.firstName || user.firstName,
      languagePreference: user.languagePreference,
      lastName: userStandRole.lastName || user.lastName,
      notify: false,
      phone: userStandRole.phone || user.phone,
    };
  };

  private handleSubmit = async () => {
    const { stand, standistRole, userStandRole } = this.props;

    const { roleCollection } = appStore;

    const roleStandist = roleCollection.findBy('reference', ROLE_STANDIST) as RoleModel;

    if (!roleStandist) {
      console.warn('roleStandist missing');
      messageErrorIntl("Une erreur s'est produite");
      return;
    }

    const values = await this.refForm.current?.validateFields();

    const userValues: any = {
      email: values.email,
      languagePreference: values.languagePreference,
      plainPassword: values.plainPassword ? values.plainPassword : undefined,
      role: roleStandist.iri,
    };

    if (!this.currentUser.id) {
      userValues.company = values.company;
      userValues.firstName = values.firstName;
      userValues.lastName = values.lastName;
      userValues.phone = values.phone;
    }

    await this.currentUser.patch(userValues);

    const userStandRoleValues: any = {
      company: values.company,
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      stand: stand?.iri,
      standistRole,
      user: this.currentUser.iri,
    };

    const emailOrPasswordChanged = this.currentUser.email !== values.email || !!values.plainPassword;

    await userStandRole.patch(userStandRoleValues);

    this.props.onSuccess?.(!!values.notify, emailOrPasswordChanged, [this.currentUser.iri]);
  };

  private handleSubmitSpecial = async () => {
    const { stand, standistRole } = this.props;

    const { roleCollection } = appStore;

    const roleStandist = roleCollection.findBy('reference', ROLE_STANDIST) as RoleModel;

    if (!roleStandist) {
      console.warn('roleStandist missing');
      messageErrorIntl("Une erreur s'est produite");
      return;
    }

    const values = await this.refForm.current?.validateFields();

    const modelValues: any = {
      company: values.company,
      email: values.email,
      firstName: values.firstName,
      languagePreference: values.languagePreference,
      lastName: values.lastName,
      phone: values.phone,
      stand: stand?.iri,
      standistRole,
    };

    const standistByStandist = new StandistByStandistModel();

    await standistByStandist.patch(modelValues);

    this.props.onSuccess?.(false, false, []);
  };

  private removeStandist = async () => {
    try {
      await this.props.userStandRole.destroy();
      this.props.onSuccess?.(false, false, []);
    } catch (error) {
      handleApiError(error);
    }
  };
}

export default StandistForm;
